// Day resources (päiväresurssit): inventory booked per calendar day (cabins, rental equipment).
// Display-side helpers for the Päivävaraukset calendar and booking dialog. The backend
// (dayreservations-call, falcon-a dayresources-utils.js) owns the real rules -- capacity and price are
// always decided there; these mirror them only to draw the calendar and preview a booking.
// See docs/features/day-resources.md. Keep this file free of React and Firebase.

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/
export const MAX_DAYS = 366

export const isValidDate = (s) => {
  if (typeof s !== 'string' || !DATE_PATTERN.test(s)) return false
  const d = new Date(s + 'T00:00:00Z')
  return !isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s
}

export const addDays = (date, n) => {
  const d = new Date(date + 'T00:00:00Z')
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}

// Every date from start to end, both included; [] when invalid or reversed.
export const daysInRange = (startDate, endDate) => {
  if (!isValidDate(startDate) || !isValidDate(endDate) || endDate < startDate) return []
  const out = []
  for (let d = startDate; d <= endDate && out.length <= MAX_DAYS; d = addDays(d, 1)) out.push(d)
  return out
}

// The dates of a month, 'YYYY-MM'.
export const monthDays = (month) => {
  const first = month + '-01'
  if (!isValidDate(first)) return []
  const out = []
  for (let d = first; d.slice(0, 7) === month; d = addDays(d, 1)) out.push(d)
  return out
}

export const shiftMonth = (month, n) => {
  const [y, m] = month.split('-').map(Number)
  const d = new Date(Date.UTC(y, m - 1 + n, 1))
  return d.toISOString().slice(0, 7)
}

export const bookingTotal = (pricePerDay, days, quantity, overrideTotal) => {
  if (overrideTotal !== undefined && overrideTotal !== null && overrideTotal !== '') return Math.round(Number(overrideTotal))
  return days * quantity * (Number(pricePerDay) || 0)
}

const isActive = (b) => !!b && b.state !== 'cancelled'

// { resourceId: { 'YYYY-MM-DD': units } } from the bookings, cancelled ones left out.
export const usageByDate = (bookings) => {
  const out = {}
  const active = (bookings || []).filter(isActive)
  active.forEach((b) => {
    const byDate = out[b.resourceId] || (out[b.resourceId] = {})
    daysInRange(b.startDate, b.endDate).forEach((d) => { byDate[d] = (byDate[d] || 0) + (b.quantity || 0) })
  })
  return out
}

// The active bookings of one resource that cover a date.
export const bookingsOn = (bookings, resourceId, date) =>
  (bookings || []).filter((b) => isActive(b) && b.resourceId === resourceId && b.startDate <= date && b.endDate >= date)

export const paidCents = (booking) =>
  ((booking && booking.payments) || []).filter((p) => p && p.type !== 'refund').reduce((s, p) => s + (Number(p.amount) || 0), 0)

export const unpaidCents = (booking) =>
  (!booking || booking.state === 'cancelled' ? 0 : Math.max(0, (booking.total || 0) - paidCents(booking)))

// 1234 -> '12,34 €'
export const formatEuro = (cents) => ((Number(cents) || 0) / 100).toFixed(2).replace('.', ',') + ' €'

// '12,5' / '12.50' / '12' -> cents; null when it is not a non-negative amount.
export const parseEuroToCents = (text) => {
  const t = String(text === undefined || text === null ? '' : text).trim().replace(/\s/g, '').replace(',', '.')
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null
  return Math.round(parseFloat(t) * 100)
}

// '5.10.2026'
export const fiDate = (date) => {
  if (!isValidDate(date)) return ''
  const [y, m, d] = date.split('-')
  return Number(d) + '.' + Number(m) + '.' + y
}

export const dateRangeText = (startDate, endDate) =>
  (startDate === endDate ? fiDate(startDate) : fiDate(startDate) + '–' + fiDate(endDate))

// The backend answers in English; known messages in Finnish. Joined messages are split on ', '.
const MESSAGES = [
  [/^full$/, 'Valittuina päivinä ei ole tarpeeksi vapaata.'],
  [/^resource not found$/, 'Päiväresurssia ei löytynyt.'],
  [/^booking not found$/, 'Varausta ei löytynyt. Lataa sivu uudelleen.'],
  [/^booking cancelled$/, 'Peruttua varausta ei voi muokata.'],
  [/^invalid startDate$/, 'Alkupäivä puuttuu tai on virheellinen.'],
  [/^invalid endDate$/, 'Loppupäivä puuttuu tai on virheellinen.'],
  [/^endDate before startDate$/, 'Loppupäivä on ennen alkupäivää.'],
  [/^too many days$/, 'Varaus voi olla enintään ' + MAX_DAYS + ' päivää.'],
  [/^invalid quantity$/, 'Määrän pitää olla vähintään 1.'],
  [/^quantity over capacity$/, 'Määrä on suurempi kuin resurssin kapasiteetti.'],
  [/^invalid overrideTotal$/, 'Hinnan pitää olla 0 tai enemmän.'],
  [/^payer missing$/, 'Valitse maksaja.'],
  [/^invalid payment amount$/, 'Maksun summan pitää olla yli 0 €.'],
  [/^invalid payment type$/, 'Valitse maksutapa.'],
]

export const translateDayMessage = (message) => {
  if (!message) return 'tuntematon virhe'
  return String(message)
    .split(', ')
    .map((m) => {
      const found = MESSAGES.find(([re]) => re.test(m.trim()))
      return found ? found[1] : m
    })
    .join(' ')
}

// The text for a refused booking: the message plus the full dates, if any.
export const errorText = (result) => {
  if (!result || !result.error) return null
  const base = translateDayMessage(result.message)
  const dates = Array.isArray(result.fullDates) && result.fullDates.length > 0
    ? ' Täynnä: ' + result.fullDates.map(fiDate).join(', ') + '.'
    : ''
  return base + dates
}

// Warning after an edit or a cancel of a booking that was already paid or invoiced for more than it
// now costs: phase 1 makes no credit notes automatically.
export const creditWarning = (result) => {
  if (!result || result.error || !result.creditNeeded) return null
  // update answers `invoiced`, cancel only `invoicedCents`
  const invoiced = !!result.invoiced || result.invoicedCents > 0
  const what = invoiced ? 'laskutettu' : 'maksettu'
  return 'Varauksesta on jo ' + what + ' ' + formatEuro(result.paidCents) + ', mutta uusi hinta on ' +
    formatEuro(result.total === undefined ? 0 : result.total) + '. Tee hyvitys ' + formatEuro(result.creditNeeded) +
    (invoiced ? ' reskontrasta.' : ' käsin.')
}

export const PAYMENT_TYPES = [
  { value: 'cash', label: 'Käteinen' },
  { value: 'card', label: 'Kortti' },
  { value: 'pos', label: 'Kassa' },
  { value: 'transfer', label: 'Tilisiirto' },
  { value: 'other', label: 'Muu' },
]
